import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageContext } from "~/providers/LanguageProvider";

import { useDisclosureStore, useTabStore } from "~/stores";
import { TypeMsg } from "./useInitDisclosuresList";
import { SEARCH_TABS } from "~/types/search.types";

type ActiveText = {
    tabText: string | null;
    disclosureText: string | null;
};

const useSearchLabel = () => {
    // Store states
    const disclosure = useDisclosureStore(state => state.disclosure);
    const activeTab = useTabStore(state => state.tab);

    const { t } = useTranslation();
    const { language } = useContext(LanguageContext);

    const [textLabel, setTextLabel] = useState<string | null>(null);
    const [currentState, setCurrentState] = useState<ActiveText>({
        tabText: null,
        disclosureText: null
    });

    const disclosureStaticMessage =
        activeTab?.key === SEARCH_TABS.FULL_DOCUMENTS
            ? t("searchForm.disclosurePartialMsg2")
            : t("searchForm.disclosurePartialMsg");
    const searchTabMessages = t("searchForm.tabMessage", { returnObjects: true }) as TypeMsg[];

    useEffect(() => {
        // Set the Search form text label with the selected tab message
        if (activeTab && activeTab.key !== currentState.tabText) {
            setCurrentState(prev => ({ ...prev, tabText: activeTab.key as string }));
        }
    }, [activeTab]);

    useEffect(() => {
        // Set the Search form text label with the selected disclosure type message
        const disclosureName = disclosure?.label[language] || "";

        if (disclosureName !== currentState.disclosureText) {
            setCurrentState(prev => ({ ...prev, disclosureText: disclosureName || null }));
        }
    }, [disclosure, language]);

    useEffect(() => {
        // Find the "full" 1-4 words/phrase message matching the selected "tabText"
        let foundMessage =
            searchTabMessages.find(item => item.key === currentState.tabText)?.message ||
            searchTabMessages[0]?.message;
        const disclosureText = currentState.disclosureText;

        // Retain using the "partial" message matching the selected "disclosure"
        if (disclosureText && currentState.tabText !== "texts") {
            foundMessage = `${disclosureStaticMessage} ${disclosureText?.toLowerCase()}...`;
        }

        if (foundMessage !== textLabel) {
            setTextLabel(foundMessage);
        }
    }, [disclosureStaticMessage, searchTabMessages, currentState]);

    return {
        textLabel
    };
};

export default useSearchLabel;
